/**
 * 채팅 응답 타이핑 버퍼(AC0-142). 앱 채팅과 어드민 출력 속도 미리보기(AC0-326)가 같은 코드를 쓰도록 ui 패키지에 둔다.
 * 모델은 문장 단위(약 40자)로 0.2초마다 덩어리를 보낸다. 받은 대로 그리면 "문장이 툭툭" 보이므로,
 * 받은 텍스트를 버퍼에 쌓고 한 글자씩 일정 간격으로 화면에 붙인다. 생성(스트림)과 타이핑은 동시에 진행된다.
 *
 * 규칙(10/22 확정):
 * - 첫 조각이 오면 첫 글자는 기다리지 않고 즉시 표시
 * - 이후 일반 글자는 charMs 간격(기본 CHAR_MS 45ms)
 * - 문장 끝(. ! ? … 뒤 공백·줄바꿈, 닫는 따옴표 포함)과 줄바꿈 뒤에는 sentencePauseMs 쉼(기본 SENTENCE_PAUSE_MS 350ms)
 * - 두 값은 서버 app_settings.chat_typing(어드민 조정, AC0-326)에서 오고, 없으면 기본값
 * - 생성이 끝나도 남은 글자를 같은 속도로 마저 친다(가속·몰아치기 없음)
 * - 시스템 "동작 줄이기"면 즉시 표시
 */

/** 일반 글자 간격 기본값(ms). 서버 기본값(settings.ts CHAT_TYPING_DEFAULT)과 같게 둔다 — 설정을 못 받았을 때의 폴백 */
export const CHAR_MS = 45;
/** 문장 끝·줄바꿈 뒤 쉼 기본값(ms). */
export const SENTENCE_PAUSE_MS = 350;

/** 타이핑 속도. 서버 /app/settings chatTyping 과 같은 모양 */
export type TypingTiming = { charMs: number; sentencePauseMs: number };
export const DEFAULT_TYPING: TypingTiming = { charMs: CHAR_MS, sentencePauseMs: SENTENCE_PAUSE_MS };
/** 백그라운드 탭 등으로 이만큼 늦어지면 밀린 시간을 버리고 지금부터 다시 센다(한 번에 쏟아지지 않게). */
const MAX_LAG_MS = 250;

const SENTENCE_END = /[.!?…。！？]/;
/** 문장 끝 뒤에 붙는 닫는 표식(대사 따옴표·지문 별표·괄호). 이 뒤에서 쉰다. */
const CLOSER = /["'”’)\]*」』]/;
const isBreak = (c: string | undefined) => c === undefined || /\s/.test(c);

/** text[i] 를 보여 준 뒤 다음 글자까지의 간격(ms). */
export function delayAfter(text: string, i: number, timing: TypingTiming = DEFAULT_TYPING): number {
  const { charMs, sentencePauseMs } = timing;
  const c = text[i];
  if (c === undefined) return charMs;
  if (c === "\n") return sentencePauseMs;
  const next = text[i + 1];
  if (SENTENCE_END.test(c)) {
    // "?!" "..." 처럼 이어지거나 닫는 표식이 오면 거기서 쉰다
    return isBreak(next) ? sentencePauseMs : charMs;
  }
  if (CLOSER.test(c) && i > 0 && SENTENCE_END.test(text[i - 1] ?? "") && isBreak(next)) return sentencePauseMs;
  return charMs;
}

/** 문자열을 index 에서 자를 때 UTF-16 서로게이트 쌍(이모지 등)을 가르지 않도록 보정한 index. */
export function safeCut(s: string, index: number): number {
  if (index <= 0 || index >= s.length) return Math.max(0, Math.min(index, s.length));
  const code = s.charCodeAt(index);
  // index 가 low surrogate 를 가리키면(= 앞 글자가 high surrogate) 한 칸 더 포함한다
  return code >= 0xdc00 && code <= 0xdfff ? index + 1 : index;
}

/** 시스템 "동작 줄이기"가 켜져 있으면(또는 rAF 가 없으면) 타이핑 없이 즉시 표시한다. */
export function prefersInstant(): boolean {
  if (typeof requestAnimationFrame !== "function") return true;
  if (typeof matchMedia !== "function") return false;
  return matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * 타이핑 버퍼. 받은 전체(received) 중 shown 글자까지만 onShow 로 내보낸다. rAF 마다 시각이 된 글자만큼 늘린다.
 * React 밖에 둔 이유: rAF 루프가 자기 자신을 다시 예약해야 해서(훅 안의 useCallback 자기 참조는 컴파일러 규칙 위반).
 */
export class TypeBuffer {
  private received = "";
  private shown = 0;
  /** 다음 글자를 보여 줄 시각. null = 아직 한 글자도 안 보임 */
  private nextAt: number | null = null;
  private drained: (() => void) | null = null;
  private raf: number | null = null;

  constructor(
    private readonly onShow: (text: string) => void,
    private readonly now: () => number = () => performance.now(),
    /** 글자마다 읽는다 — 설정이 바뀌면(어드민 미리보기 슬라이더) 다음 글자부터 새 속도 */
    private readonly timing: () => TypingTiming = () => DEFAULT_TYPING,
  ) {}

  reset(): void {
    this.cancel();
    this.received = "";
    this.shown = 0;
    this.nextAt = null;
    this.drained = null;
  }

  push(text: string): void {
    if (!text) return;
    this.received += text;
    if (prefersInstant()) {
      this.revealAll();
      return;
    }
    // 첫 글자는 즉시(다음 프레임을 기다리지 않는다)
    if (this.shown === 0) this.step();
    if (!this.settle()) this.schedule();
  }

  /** message.done: 서버 확정 본문으로 맞추고, 남은 글자를 같은 속도로 다 친 뒤 resolve. */
  finish(finalText: string): Promise<void> {
    return new Promise((resolve) => {
      const shownText = this.received.slice(0, this.shown);
      this.received = finalText;
      // 확정 본문이 보이던 앞부분과 다르면(서버 정규화) 이어 칠 수 없으므로 즉시 확정
      if (!finalText.startsWith(shownText) || prefersInstant()) {
        this.revealAll();
        resolve();
        return;
      }
      this.drained = resolve;
      if (!this.settle()) this.schedule();
    });
  }

  /** 남은 글자를 즉시 전부 표시(동작 줄이기·끊김 복구·오류). */
  revealAll(): void {
    this.cancel();
    this.shown = this.received.length;
    this.onShow(this.received);
    this.settle();
  }

  cancel(): void {
    if (this.raf !== null) cancelAnimationFrame(this.raf);
    this.raf = null;
  }

  private schedule(): void {
    if (this.raf === null) this.raf = requestAnimationFrame(this.frame);
  }

  /** 다 보였으면 대기 중인 finish 를 풀어 준다. nextAt 은 유지(문장 끝 쉼이 다음 조각에도 이어지게). */
  private settle(): boolean {
    if (this.shown < this.received.length) return false;
    const done = this.drained;
    this.drained = null;
    done?.();
    return true;
  }

  /** 지금 시각까지 보여 줄 차례가 된 글자를 붙인다. */
  private step(): void {
    const now = this.now();
    if (this.nextAt === null || now - this.nextAt > MAX_LAG_MS) this.nextAt = now;
    const before = this.shown;
    while (this.shown < this.received.length && now >= this.nextAt) {
      const i = this.shown;
      this.shown = safeCut(this.received, i + 1);
      this.nextAt += delayAfter(this.received, this.shown - 1, this.timing());
    }
    if (this.shown !== before) this.onShow(this.received.slice(0, this.shown));
  }

  private readonly frame = (): void => {
    this.raf = null;
    this.step();
    if (!this.settle()) this.schedule();
  };
}
